import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '../PageLayout/Footer';
import markdownToHtml from '../../Utils/markdownToHtml';
import { INFO_PAGE_LINKS, INFO_PAGE_REGISTRY } from './infoPagesConfig';

const INFO_PAGES_API_BASE = 'https://backproject.meanc.net/meanc/info-pages';

const InfoPage = () => {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const normalizedSlug = slug.toLowerCase();
  const pageMeta = INFO_PAGE_REGISTRY[normalizedSlug];
  const [markdown, setMarkdown] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [apiMenuPages, setApiMenuPages] = useState([]);
  const [apiPageMeta, setApiPageMeta] = useState(null);
  const [isMenuLoading, setIsMenuLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setIsMenuLoading(true);

    fetch(`${INFO_PAGES_API_BASE}/`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((payload) => {
        if (!isMounted) return;
        const payloadList = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.results)
            ? payload.results
            : [];
        setApiMenuPages(payloadList);
      })
      .catch(() => {
        if (isMounted) {
          setApiMenuPages([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsMenuLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!pageMeta) {
      setIsLoading(false);
      setLoadError('missing');
      return;
    }

    let isMounted = true;
    setLoadError('');
    setIsLoading(true);
    setApiPageMeta(null);

    const fetchStaticMarkdown = async () => {
      if (!pageMeta.markdownUrl) {
        throw new Error('Missing static markdown fallback');
      }
      const fallbackResponse = await fetch(pageMeta.markdownUrl);
      const fallbackText = await fallbackResponse.text();
      if (!fallbackResponse.ok) {
        throw new Error('Unable to load fallback content');
      }
      if (isMounted) {
        setMarkdown(fallbackText);
        setIsLoading(false);
      }
    };

    const fetchFromApi = async () => {
      const requestSlug = pageMeta.apiSlug || normalizedSlug;
      const response = await fetch(`${INFO_PAGES_API_BASE}/${requestSlug}/`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      const data = await response.json();
      const content = data?.content || data?.markdown || data?.body || '';
      if (!content) {
        throw new Error('Empty response content');
      }
      if (isMounted) {
        setMarkdown(content);
        setIsLoading(false);
        setApiPageMeta({
          title: data?.title,
          summary: data?.summary,
          slug: data?.slug,
        });
      }
    };

    fetchFromApi().catch(() => {
      fetchStaticMarkdown().catch(() => {
        if (isMounted) {
          setLoadError('content');
          setIsLoading(false);
        }
      });
    });

    return () => {
      isMounted = false;
    };
  }, [pageMeta, normalizedSlug]);

  useEffect(() => {
    const nextTitle = pageMeta?.title || apiPageMeta?.title;
    if (nextTitle) {
      document.title = `Meanc | ${nextTitle}`;
    }
  }, [pageMeta, apiPageMeta]);

  const parsedHtml = useMemo(() => markdownToHtml(markdown), [markdown]);

  const navLinks = useMemo(() => {
    if (apiMenuPages.length > 0) {
      return apiMenuPages.map((page) => ({
        slug: page.slug,
        label: page.title || page.slug,
      }));
    }
    return INFO_PAGE_LINKS.map((link) => ({
      slug: link.slug,
      label: t(link.labelKey),
    }));
  }, [apiMenuPages, t]);

  const heroStyle = pageMeta
    ? {
        backgroundImage: `linear-gradient(135deg, ${pageMeta.accent}ee, rgba(18, 18, 18, 0.85)), url(${pageMeta.heroImage})`,
      }
    : {
        background: 'linear-gradient(135deg, #1b2735, #090a0f)',
      };

  const heroTitle = pageMeta?.title || apiPageMeta?.title || 'Resource Center';
  const heroSummary = pageMeta?.summary || apiPageMeta?.summary || '';

  const renderContentState = () => {
    if (isLoading) {
      return <p className="info-page__status">Loading thoughtful content…</p>;
    }

    if (loadError === 'missing') {
      return (
        <div className="info-page__status">
          <p>We could not find that resource.</p>
          <button type="button" className="info-page__button" onClick={() => navigate('/')}>Back to home</button>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="info-page__status">
          <p>Something went wrong while loading this page. Please refresh to try again.</p>
        </div>
      );
    }

    return (
      <article className="info-page__article" dangerouslySetInnerHTML={{ __html: parsedHtml }} />
    );
  };

  return (
    <div className="info-page">
      <section className="info-page__hero" style={heroStyle}>
        <div className="info-page__hero-content">
          <p className="info-page__eyebrow">Meanc Info Center</p>
          <h1>{heroTitle}</h1>
          {heroSummary && <p className="info-page__summary">{heroSummary}</p>}
          <div className="info-page__hero-actions">
            <button type="button" className="info-page__button" onClick={() => navigate('/')}>Back to home</button>
            <a className="info-page__button info-page__button--ghost" href="mailto:services@meanc.net">Talk to support</a>
          </div>
        </div>
      </section>
      <div className="info-page__brand" aria-hidden="true">
        <img src={`${process.env.PUBLIC_URL}/images/img2/meanclogo4.png`} alt="" />
      </div>

      <div className="info-page__body">
        <nav className="info-page__menu" aria-label="Information pages">
          {isMenuLoading && <p className="info-page__menu-loading">Syncing index…</p>}
          {navLinks.map((link) => (
            <Link
              key={link.slug}
              to={`/info/${link.slug}`}
              className={`info-page__menu-link${link.slug === normalizedSlug ? ' is-active' : ''}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="info-page__card">
          {pageMeta && (
            <header className="info-page__card-header">
              <span className="info-page__status-pill">Live</span>
              <p>Last updated: {new Date().getFullYear()}</p>
            </header>
          )}
          {renderContentState()}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InfoPage;
